import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type * as type from "@vTypes";
import getDailyShift, { parseManpowerFromApi } from "../service/getDailyShift";

interface UseManpowerParams {
  manpowerOid: string | undefined;
  manpowerGid: string | undefined;
  year: number | undefined;
  month: number | undefined;
  isAdmin: boolean;
  selectedOfficeId: string | null;
  selectedGroupId: string | null;
  loginUser: { office_id: string; group_id: string } | null;
}

export default function useManpower({
  manpowerOid,
  manpowerGid,
  year,
  month,
  isAdmin,
  selectedOfficeId,
  selectedGroupId,
  loginUser,
}: UseManpowerParams) {
  const { data: manpowerData } = useQuery({
    queryKey: ["manpower", manpowerOid, manpowerGid, year, month],
    queryFn: async () => {
      if (!manpowerOid || !manpowerGid || !year || !month) {
        throw new Error("필수 파라미터가 누락되었습니다.");
      }
      return getDailyShift({
        office_id: manpowerOid,
        group_id: manpowerGid,
        year,
        month,
      });
    },
    enabled: !!(manpowerOid && manpowerGid && year && month),
    staleTime: 1000 * 60 * 5, // 5분간 캐시
  });

  const parsedManpower = useMemo(() => {
    if (!manpowerData) return null;
    return parseManpowerFromApi(manpowerData);
  }, [manpowerData]);

  // 만들기 페이지용: 패칭 완료된 데이터만 사용 (동기화는 패칭 이후에만)
  const manPower_monthly = useMemo<type.ManPowerMonthly | null>(() => {
    if (!parsedManpower) return null;
    return parsedManpower.monthly as type.ManPowerMonthly;
  }, [parsedManpower]);
  const manPower_daily = useMemo<type.ManPowerDaily | null>(() => {
    if (!parsedManpower) return null;
    return parsedManpower.daily as type.ManPowerDaily;
  }, [parsedManpower]);
  const useMaxManpower = useMemo(() => {
    return parsedManpower?.useMax ?? false;
  }, [parsedManpower]);

  return {
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    manpowerOid:
      (isAdmin ? selectedOfficeId : loginUser?.office_id) ??
      manpowerOid ??
      undefined,
    manpowerGid:
      (isAdmin ? selectedGroupId : loginUser?.group_id) ??
      manpowerGid ??
      undefined,
    year,
    month,
  };
}
